import { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import numbro from 'numbro';

import { StationItemNo } from '../../../../constants/Games/gameStation';
import { getStationVisual } from '../../../../constants/Games/gameStationUtils';
import { getGameTypeDetails } from '../../../../constants/Games/gameType';
import { Icons } from '../../../../constants/icons';
import { SVGIconType } from '../../../../constants/svgIcon';
import { Game } from '../../../../models/GameStore';
import { Profile } from '../../../../models/UserStore';
import { getMaskedString } from '../../../../utils/game';
import { AvatarNode } from '../../AvatarNode';
import { FillPNGIcon } from '../../FillPNGIcon';
import { SeparatorComponent } from '../../SeparatorComponent';
import { SvgIcon } from '../../SVGIcon';

import './AssistedStationCell.css';

export type AssistedStationCellProps = {
  game: Game;
  stationId: StationItemNo;
  name: string | undefined;
  index: number;
  prefixCode?: string;
  iconKey: string;
  playCount: number;
  userProfile: Profile;
  recordScore: number | undefined;
  recordDuration: string | undefined;
  isUnlocked: boolean;
  onClickPlay: () => void;
  onClickInfo: () => void;
  onClickAnalysis?: () => void;
  onClickShare?: () => void;
};

export const AssistedStationCell = memo((props: AssistedStationCellProps) => {
  const {
    game,
    stationId,
    // name,
    // index,
    iconKey,
    prefixCode,
    playCount,
    userProfile,
    recordScore,
    recordDuration,
    isUnlocked,
    onClickPlay,
    onClickInfo,
    onClickAnalysis,
    onClickShare,
  } = props;
  const { t } = useTranslation();

  //game data
  const gameTitle =
    game.i18nGameData.resources[game.i18nGameData.defaultLocale]?.title;

  const gameVariantsArray =
    game.i18nGameData.resources[game.i18nGameData.defaultLocale]?.variants ||
    [];

  const gameTypeIcon = iconKey ? getGameTypeDetails(iconKey).icon : undefined;
  const gameTypeSvgIcon = iconKey
    ? getGameTypeDetails(iconKey).svgIcon
    : undefined;
  const gameTypeName = iconKey
    ? getGameTypeDetails(iconKey).displayName
    : undefined;

  return (
    <div className="assisted-station-cell" onClick={onClickPlay}>
      <div className="centered-container">
        <div className="lower-row">
          <div className="component">
            <SvgIcon
              name={SVGIconType.ProgressArrow}
              width={32}
              height={32}
              color={isUnlocked ? '#ffffff' : '#b2b2b2'}
              style={{
                filter: 'drop-shadow(0px 0px 1px #00000050)', // Drop shadow
                marginBottom: -6,
                opacity: 0.85,
              }}
            />
            <div
              className={`${'station-icon'} ${isUnlocked ? '' : 'gray-scale'}`}
            >
              <img
                src={getStationVisual(stationId)?.filePrefix}
                className="large-icon"
              />
            </div>
            {/* <div className="text-overlay">{name ?? index}</div> */}
          </div>
          <div
            className={`game-component ${isUnlocked ? 'unlocked' : 'locked'}`}
          >
            <div className="upper-row">
              <div className="text-row">
                <div className="text-content">
                  <div className="type-title">
                    <span className="prefixCode">{prefixCode}</span>
                    {`${t('Interactive Learning')}`}
                  </div>
                  <div className={`descriptionForTitle grey-text`}>
                    {gameTypeSvgIcon ? (
                      <span className="icon">
                        <SvgIcon
                          name={gameTypeSvgIcon}
                          height={20}
                          width={20}
                          color={'#cccccc'}
                        />
                      </span>
                    ) : gameTypeIcon && Icons[gameTypeIcon] ? (
                      <span className="icon">
                        <FillPNGIcon
                          src={Icons[gameTypeIcon]}
                          color="#cccccc"
                          size={20}
                        />
                      </span>
                    ) : null}
                    {gameTypeName ? t(gameTypeName) : null}
                  </div>
                </div>
              </div>
              <div
                className="info-container"
                onClick={(e) => {
                  e.stopPropagation(); // Stop the event propagation
                  onClickInfo();
                }}
              >
                <SvgIcon
                  name={SVGIconType.GameInstruction}
                  className={`info-icon grey-text`}
                />
              </div>
            </div>
            <div className="upper-part">
              <div className="left-part">
                <div className="title">{gameTitle}</div>
                <div className="description">
                  {getMaskedString(gameVariantsArray)}
                </div>
                <div className="info">
                  <SeparatorComponent
                    separator={<p className={`separate-text grey-text`}>·</p>}
                  >
                    <div className={`reward-text grey-text`}>
                      <Trans
                        i18nKey="{count} Plays"
                        values={{
                          count: numbro(playCount).format({
                            thousandSeparated: true,
                          }),
                        }}
                      />
                    </div>
                  </SeparatorComponent>
                </div>
              </div>
              <div className="right-part">
                {/* <SvgIcon
                  name={SVGIconType.More}
                  className={`more-icon grey-text`}
                /> */}

                {isUnlocked ? (
                  <div
                    className="play-button"
                    onClick={(e) => {
                      e.stopPropagation(); // Stop the event propagation
                      onClickPlay();
                    }}
                  >
                    <SvgIcon
                      name={SVGIconType.Play}
                      width={24}
                      height={24}
                      color="#ff9500"
                    />
                  </div>
                ) : null}

                {isUnlocked && onClickShare && (
                  <div
                    className="share-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClickShare();
                    }}
                  >
                    <SvgIcon
                      name={SVGIconType.Share}
                      width={24}
                      height={24}
                      color="#fff"
                    />
                  </div>
                )}
              </div>
            </div>
            {recordScore !== undefined || recordDuration !== undefined ? (
              <div className="lower-part">
                <AvatarNode profile={userProfile} size={28} />
                <div className="reward">
                  <SvgIcon
                    name={SVGIconType.Score}
                    className={`reward-icon grey-text`}
                  />
                  <div className={`reward-text grey-text`}>
                    {Math.floor(recordScore ?? 0)}
                  </div>
                </div>
                <div className="reward">
                  <SvgIcon
                    name={SVGIconType.Timer}
                    className={`reward-icon grey-text`}
                  />
                  <div className={`reward-text grey-text`}>
                    {recordDuration}
                  </div>
                </div>
                {onClickAnalysis && (
                  <div
                    className="analysis-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClickAnalysis();
                    }}
                  >
                    <SvgIcon
                      name={SVGIconType.ScoreAnalysis}
                      color={'#fff'}
                      width={20}
                      height={20}
                    />
                    <SvgIcon
                      name={SVGIconType.Arrow}
                      color={'#fff'}
                      width={20}
                      height={20}
                    />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
});
